import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { LegacyImage as Image } from '@/atoms/LegacyImage'
import { CaptionMD, EyebrowSM, HeadingMD, ParagraphMD } from '@/atoms/Text'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'

interface CollectiblesMarketplacePageProps {
  locale: string
}

export const CollectiblesMarketplacePage: NextPageWithLayout<CollectiblesMarketplacePageProps> = ({ locale }) => {
  const { t } = useTranslate('collectibles-marketplace')

  const title = t('collectiblesMarketplace', 'Collectibles Marketplace')
  const description = t(
    'cmCurrentlyUnderConstruction',
    'The Collectibles Marketplace is currently under construction. To view your collectibles, go to the Profile Page.',
  )

  return (
    <>
      <Seo
        {...getBasicPageMetaData({
          locale,
          path: paths.collectiblesMarketplace.index,
          title,
          description,
          cloudinaryImagePath: 'v1756153598/angel-studios/seo/angel-opengraph-5',
          cloudinaryTransformation: '',
        })}
      />
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-white px-8 text-center">
        <div
          className={classNames(
            'flex items-center justify-center flex-col gap-6',
            'w-full max-w-[480px]',
            'lg:w-[480px]',
          )}
        >
          <Image
            alt=""
            src="/w_240,q_auto/v1683831579/angel-studios/collectibles/under-construction-01_2x.webp"
            width={240}
            height={145}
          />
          <div>
            <EyebrowSM color="core-gray-600" className="xl:photon-eyebrow-md mb-1">
              <Translate t={t} i18nKey="underConstruction">
                Under Construction
              </Translate>
            </EyebrowSM>
            <HeadingMD className="xl:photon-heading-lg shrink-0" weight="bold">
              <Translate t={t} i18nKey="collectiblesMarketplace">
                Collectibles Marketplace
              </Translate>
            </HeadingMD>
          </div>

          <ParagraphMD className="shrink-1 xl:photon-paragraph-lg text-black">{description}</ParagraphMD>

          <LinkButton href={paths.account.collectibles} variant="core-gray-800">
            <CaptionMD weight="bold" className="xl:photon-caption-lg">
              <Translate i18nKey="goToProfile" t={t}>
                Go to Profile
              </Translate>
            </CaptionMD>
          </LinkButton>
        </div>
      </div>
    </>
  )
}

export const getStaticProps = getStaticPropsErrorHandler<CollectiblesMarketplacePageProps>(
  { filePath: 'pages/[locale]/collectibles-marketplace/index.tsx' },
  async ({ locale }) => {
    return {
      props: {
        locale,
        ...(await loadTranslations(locale, ['common', 'collectibles-marketplace'])),
      },
      revalidate: Durations.ONE_DAY_IN_SECONDS,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

CollectiblesMarketplacePage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export default CollectiblesMarketplacePage
